import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import logo from "@/assets/tribeo-logo.svg";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { scrollToHash } from "@/lib/scrollToHash";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { track, REGISTER_URL, LOGIN_URL } from "@/lib/analytics";

const Header = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id: string) => {
    setOpen(false);
    const home = lang === "en" ? "/en" : "/";
    if (location.pathname !== home) {
      navigate(`${home}#${id}`);
      setTimeout(() => scrollToHash(id), 80);
    } else {
      scrollToHash(id);
    }
  };

  const goTo = (path: string) => {
    setOpen(false);
    navigate(path);
    window.scrollTo({ top: 0 });
  };

  const isEn = lang === "en";
  const items: { label: string; section?: string; path?: string }[] = [
    { label: t.solutionsNav.broker, path: isEn ? "/en/for-agents" : "/voor-makelaars" },
    { label: t.solutionsNav.supplier, path: isEn ? "/en/for-vendors" : "/voor-leveranciers" },
    { label: isEn ? "For clients" : "Voor klanten", path: isEn ? "/en/home-advisor" : "/wooncoach" },
    { label: t.nav.pricing, section: "pricing" },
    { label: t.nav.about, path: isEn ? "/en/about" : "/about" },
    { label: t.nav.contact, path: isEn ? "/en/contact" : "/contact" },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-subtle border-b border-border/40" : "bg-transparent"
      }`}
    >
      <div className="container flex h-16 items-center justify-between">
        <button onClick={() => goTo(isEn ? "/en" : "/")} className="flex items-center" aria-label="Tribeo home">
          <img src={logo} alt="Tribeo" className="h-7 w-auto" />
        </button>

        <nav className="hidden md:flex items-center gap-6 text-sm font-bold text-foreground/80">
          {items.map((it) => (
            <button
              key={it.label}
              onClick={() => (it.path ? goTo(it.path) : goSection(it.section!))}
              className="hover:text-foreground transition-colors"
            >
              {it.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden lg:flex items-center rounded-full border border-border bg-background/60 p-0.5 text-xs font-bold">
            <button
              onClick={() => setLang("nl")}
              className={`px-2.5 py-1 rounded-full transition-colors ${lang === "nl" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              aria-pressed={lang === "nl"}
            >
              NL
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-2.5 py-1 rounded-full transition-colors ${lang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              aria-pressed={lang === "en"}
            >
              EN
            </button>
          </div>
          <a
            href={LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("login_click", "header")}
            className="hidden sm:inline-flex text-sm font-bold text-foreground/80 hover:text-foreground px-3"
          >
            {t.nav.login}
          </a>
          <a
            href={REGISTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("register_click", "header")}
            className="hidden sm:inline-flex"
          >
            <Button size="sm" className="rounded-full font-bold">{t.nav.register}</Button>
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button size="icon" variant="ghost" className="md:hidden" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-8 flex flex-col gap-4">
                {items.map((it) => (
                  <button
                    key={it.label}
                    onClick={() => (it.path ? goTo(it.path) : goSection(it.section!))}
                    className="text-left text-base font-bold py-2 border-b border-border"
                  >
                    {it.label}
                  </button>
                ))}
                <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("register_click", "mobile_menu")}>
                  <Button className="mt-4 w-full rounded-full">{t.nav.register}</Button>
                </a>
                <a href={LOGIN_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("login_click", "mobile_menu")}>
                  <Button variant="outline" className="w-full rounded-full">{t.nav.login}</Button>
                </a>
                <div className="mt-2 inline-flex items-center self-start rounded-full border border-border p-0.5 text-xs font-bold">
                  <button onClick={() => setLang("nl")} className={`px-3 py-1 rounded-full ${lang === "nl" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>NL</button>
                  <button onClick={() => setLang("en")} className={`px-3 py-1 rounded-full ${lang === "en" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>EN</button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
